import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CONFIG } from '../utils/config';
import { rememberPendingEmail } from '../services/accountService';
import { trackSignup } from '../utils/analytics';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formHidden, setFormHidden] = useState(false);
  const { content, loading: contentLoading } = useContent('signup');
  const { isDark } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirm) {
      setError(content.feedback.passwordMismatch);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${CONFIG.BASE_URL}/sign-up`, {
        mode: 'cors',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mail: email, username, password }),
      });

      const contentType = res.headers.get('content-type') || '';
      const parseJson = contentType.includes('application/json');
      const data = parseJson ? await res.json() : {};

      if (!res.ok) {
        const msg = data?.error || `${content.feedback.signupFailed} (${res.status})`;
        throw new Error(msg);
      }

      const token = data?.access;
      if (!token) throw new Error(content.feedback.invalidResponse);

      localStorage.removeItem('quikr_access_token');
      document.cookie = 'quikr_access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
      localStorage.setItem('quikr_email_verified', 'false');

      rememberPendingEmail(email.trim().toLowerCase());
      setSuccess(verificationMessage);
      setFormHidden(true);
      trackSignup('email');

      setEmail('');
      setUsername('');
      setPassword('');
      setConfirm('');
    } catch (err) {
      setError(err.message || content.genericError);
    } finally {
      setIsLoading(false);
    }
  };


  if (contentLoading || !content) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const verificationMessage = content.success;

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <div
        className="absolute inset-0 bg-no-repeat chameleon-background pointer-events-none"
        style={{
          backgroundImage: 'url(/images/Cameleon_HD.jpg)',
          backgroundPosition: 'calc(100% + 100px) center'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-l from-dark-900/0 via-dark-900/70 to-dark-900 pointer-events-none"></div>
      <div className="relative max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-lg">Q</span>
            </div>
            <span className="text-2xl font-bold text-white">QUIKR.IT</span>
          </Link>
          <p className="text-gray-400 mt-2">{content.pageTitle}</p>
        </div>

        <div className="bg-dark-800 p-8 rounded-xl border border-dark-700">
          {!formHidden ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-900/40 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.emailLabel}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder={content.form.emailPlaceholder}
                />
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                  {content.form.usernameLabel}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder={content.form.usernamePlaceholder}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.passwordLabel}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder={content.form.passwordPlaceholder}
                />
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.confirmLabel}
                </label>
                <input
                  id="confirm"
                  name="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder={content.form.confirmPlaceholder}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-black bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
              >
                {isLoading ? content.form.submitting : content.form.submitButton }
              </button>
            </form>
          ) : (
            <div className="space-y-6 text-center">
              {success && (
                <div className="bg-green-900/40 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}
              <Link
                to="/login"
                className="inline-flex justify-center w-full py-3 px-4 rounded-lg border border-primary-500 text-primary-300 hover:bg-primary-500/10 transition-colors duration-200 font-medium"
              >
                Se connecter
              </Link>
            </div>
          )}

          {!formHidden && (
            <p className="mt-6 text-center text-sm text-gray-400">
		  {content.footer.alreadyAccount}{' '}
              <Link to="/login" className="text-primary-400 hover:text-primary-300">
		  {content.footer.login}
              </Link>
            </p>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-400 hover:text-white text-sm">
	  {content.footer.backHome}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
